import React from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { Task } from '@paintscout/api'
import type { StyleClasses } from '@paintscout/ui'
import {
  ActionButton,
  ConfirmationDialog,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Typography,
  useDialogs
} from '@paintscout/ui'
import { useSnackbar } from 'notistack'
import { useTaskMutations, useLocalStorage } from 'shared/hooks'
import TaskTable from './TaskTable'
import { Sentry } from 'src/util/appSentry'
import classNames from 'classnames'

const useStyles = makeStyles<Theme, TemplateTaskTablesProps>(
  (theme) => ({
    panelRoot: {
      borderBottom: `1px solid ${theme.palette.divider}`
      // annoying to do, leaving out for now
      // '&.Mui-expanded': {
      //   marginBottom: theme.spacing(6)
      // }
    },
    titleAdding: {
      color: theme.palette.grey[500]
    },
    panelSummaryRoot: {
      padding: theme.spacing(4, 2),
      flexDirection: 'row-reverse',
      '&:hover': {
        backgroundColor: theme.palette.grey[100]
      },
      '&.Mui-expanded': {
        backgroundColor: theme.palette.grey[100]
      },
      '&.Mui-disabled': {
        paddingRight: theme.spacing(2)
      }
    },
    panelSummaryContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    panelExpandIcon: {
      marginRight: 0,
      marginLeft: -12,
      transform: 'rotate(-90deg)',
      '&.Mui-expanded': {
        transform: 'rotate(0deg)'
      }
    },
    taskCountChip: {
      marginLeft: theme.spacing(1),
      padding: theme.spacing(0.5, 1),
      borderRadius: 12,
      backgroundColor: theme.palette.grey[200],
      fontSize: '0.75rem',
      verticalAlign: 'middle'
    },
    panelDetailsRoot: {
      display: 'flex',
      flexDirection: 'column'
    },
    taskDocItem: {
      position: 'relative',
      paddingLeft: theme.spacing(4),
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: theme.spacing(3),
        height: `calc(100% - 20px)`,
        borderLeft: `3px solid ${theme.palette.divider}`,
        marginTop: 10
      }
    }
  }),
  { name: 'TemplateTaskTables' }
)

export interface TemplateTaskTablesProps {
  classes?: StyleClasses<typeof useStyles>
  templateTasks: { [key: string]: Task[] }
  orderedTemplateKeys: string[]
  loading?: boolean
  dense?: boolean
  enableFade?: boolean
  view?: 'upcoming' | 'overdue' | 'completed' | 'all'
  addingTemplate?: { key: string; label: string } | null
  onDelete?: (ids: string[]) => void
  onEdit?: (task: any) => void
  onComplete?: () => void
  onReorderTaskTemplates: (newOrder: string[]) => void
}

interface TemplateTaskTableProps {
  templateId: string
  templateTasks: Task[]
  titleOverride?: string
  classes: ReturnType<typeof useStyles>
  loading?: boolean
  dense?: boolean
  enableFade?: boolean
  view?: 'upcoming' | 'overdue' | 'completed' | 'all'
  isFirst?: boolean
  isLast?: boolean
  isAdding?: boolean
  expanded: boolean
  onToggleExpanded: (val: boolean) => void
  onDeleteTemplate: () => void
  onDelete?: (ids: string[]) => void
  onEdit?: (task: any) => void
  onComplete?: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

function TemplateTaskTable({
  templateId,
  templateTasks,
  titleOverride,
  classes,
  loading,
  dense,
  enableFade,
  view,
  isFirst,
  isLast,
  isAdding,
  expanded,
  onToggleExpanded,
  onDeleteTemplate,
  onDelete,
  onEdit,
  onComplete,
  onMoveUp,
  onMoveDown
}: TemplateTaskTableProps) {
  const title = titleOverride || templateTasks[0]?.template?.templateLabel || ''
  const taskCount = templateTasks.length
  const completedCount = templateTasks.filter((task) => task.completed).length
  const { openDialog, dismissDialog } = useDialogs()
  const { deleteTasks } = useTaskMutations()
  const { enqueueSnackbar } = useSnackbar()

  const handleDeleteTaskTemplate = (taskIds: string[]) => {
    openDialog(ConfirmationDialog, {
      message: <span>This will permanently delete this Checklist.</span>,
      onConfirm: async () => {
        try {
          await deleteTasks(taskIds)
          onDeleteTemplate()
          if (onDelete) {
            onDelete(taskIds)
          }
          dismissDialog()
        } catch (error) {
          console.log(error)
          Sentry.captureException(error, { tags: { taskIds: taskIds.join(',') } })
          enqueueSnackbar('Unable to delete Checklist', { variant: 'error' })
        }
      },
      onCancel: dismissDialog
    })
  }

  const sortedTasks = [...templateTasks].sort((a, b) => {
    const indexA = a.template?.index ?? 0
    const indexB = b.template?.index ?? 0
    return indexA - indexB
  })

  const actions = [
    ...(!isFirst ? [{ key: 'move-up', label: 'Move Up' }] : []),
    ...(!isLast ? [{ key: 'move-down', label: 'Move Down' }] : []),
    { key: 'delete-list', label: 'Delete', scary: true }
  ]

  return (
    <ExpansionPanel
      key={templateId}
      expanded={isAdding ? false : expanded}
      disabled={isAdding}
      onChange={(_, isExpanded) => onToggleExpanded(isExpanded)}
      classes={{ root: classes.panelRoot }}
    >
      <ExpansionPanelSummary
        classes={{
          root: classes.panelSummaryRoot,
          content: classes.panelSummaryContent,
          expandIcon: classes.panelExpandIcon
        }}
      >
        <Typography variant={'h4'} className={classNames({ [classes.titleAdding]: isAdding })}>
          {title}
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ActionButton
            disabled={isAdding}
            actions={actions}
            onActionClick={(_, actionKey) => {
              if (actionKey === 'move-up') {
                onMoveUp()
              } else if (actionKey === 'move-down') {
                onMoveDown()
              } else if (actionKey === 'delete-list') {
                handleDeleteTaskTemplate(templateTasks.map((t) => t._id).filter(Boolean) as string[])
              }
            }}
          />
          <span className={classes.taskCountChip}>{`${completedCount}/${taskCount}`}</span>
        </div>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails classes={{ root: classes.panelDetailsRoot }}>
        <TaskTable
          classes={{ taskDocItem: classes.taskDocItem }}
          tasks={sortedTasks}
          loading={loading}
          dense={dense}
          totalRows={taskCount}
          page={1}
          rowsPerPage={taskCount}
          pages={1}
          enableFade={enableFade}
          view={view}
          disablePagination
          actions={{
            edit: true,
            viewDeal: true,
            delete: true
          }}
          actionsForRow={(row) => ({
            delete: true,
            viewDeal: !!row.dealId,
            edit: true
          })}
          onDelete={onDelete}
          onEdit={onEdit}
          onComplete={onComplete}
        />
      </ExpansionPanelDetails>
    </ExpansionPanel>
  )
}

function TemplateTaskTables(props: TemplateTaskTablesProps) {
  const {
    templateTasks,
    orderedTemplateKeys,
    loading,
    dense,
    enableFade,
    view,
    addingTemplate,
    onDelete,
    onEdit,
    onComplete,
    onReorderTaskTemplates
  } = props
  const classes = useStyles(props)

  const [expandedMap, setExpandedMap] = useLocalStorage<Record<string, boolean>>('template-task-tables-expanded', {})

  const handleToggleExpanded = (templateId: string, val: boolean) => {
    setExpandedMap((prev) => ({ ...prev, [templateId]: val }))
  }

  const handleDeleteTemplate = (templateId: string) => {
    setExpandedMap((prev) => {
      const next = { ...prev }
      delete next[templateId]
      return next
    })
  }

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const newKeys = [...orderedTemplateKeys]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newKeys[index], newKeys[swapIndex]] = [newKeys[swapIndex], newKeys[index]]
    onReorderTaskTemplates(newKeys)
  }

  const addingKey = addingTemplate?.key
  const showPlaceholder = addingKey && !orderedTemplateKeys.includes(addingKey)

  return (
    <div className={classes.root}>
      {orderedTemplateKeys.map((templateId, index) => {
        if (!templateTasks[templateId]) return null
        return (
          <TemplateTaskTable
            key={templateId}
            templateId={templateId}
            templateTasks={templateTasks[templateId]}
            classes={classes}
            loading={loading}
            dense={dense}
            enableFade={enableFade}
            view={view}
            isFirst={index === 0}
            isLast={index === orderedTemplateKeys.length - 1 && !showPlaceholder}
            isAdding={addingKey === templateId}
            expanded={expandedMap[templateId] ?? true}
            onToggleExpanded={(val) => handleToggleExpanded(templateId, val)}
            onDeleteTemplate={() => handleDeleteTemplate(templateId)}
            onDelete={onDelete}
            onEdit={onEdit}
            onComplete={onComplete}
            onMoveUp={() => handleMove(index, 'up')}
            onMoveDown={() => handleMove(index, 'down')}
          />
        )
      })}
      {showPlaceholder && (
        <TemplateTaskTable
          key={addingKey}
          templateId={addingKey}
          templateTasks={[]}
          titleOverride={addingTemplate.label}
          classes={classes}
          isFirst={orderedTemplateKeys.length === 0}
          isLast={true}
          isAdding={true}
          expanded={false}
          onToggleExpanded={() => null}
          onDeleteTemplate={() => null}
          onMoveUp={() => {}}
          onMoveDown={() => {}}
        />
      )}
    </div>
  )
}

export default TemplateTaskTables
